import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { visuallyHidden } from '@mui/utils'
import clsx from 'clsx'
import {
  Link as RouterLink,
  Redirect,
  useHistory,
  useParams,
} from 'react-router-dom'
import Avatar from '@material-ui/core/Avatar'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconButton from '@material-ui/core/IconButton'
import LinearProgress from '@material-ui/core/LinearProgress'
import TableCell from '@material-ui/core/TableCell'
import TableRow from '@material-ui/core/TableRow'
import Tooltip from '@material-ui/core/Tooltip'
import Typography from '@material-ui/core/Typography'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import Error from 'components/error'
import CampaignOutlinedIcon from 'components/material-symbol/icons/Campaign'
import FileCopyIcon from 'components/material-symbol/icons/FileCopy'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import { PageHeader } from 'components/page-header'
import SectionTitle from 'components/section-title'
import EnhancedTable from 'components/tables/enhanced-table'
import { useAuth } from 'lib/auth'
import { useIsMobile } from 'lib/hooks'
import { getInitials } from 'lib/text-display'
import { useSnackbar, useSnackbarError, useSnackbarSuccess } from 'lib/snackbar'
import StatCard from 'pages/pga-coach/coach-analytics/components/stat-card'
import useAnalyticsStyles from 'pages/pga-coach/coach-analytics/styles'
import { formatCount } from 'pages/pga-coach/coach-analytics/utils'
import { useStudentListNameAgeStyles } from 'pages/pga-coach/students/student-list-name-age-styles'
import {
  useCancelCoachCampaign,
  useCoachCampaignResults,
  useDuplicateCoachCampaign,
} from './api-hooks'
import { StatusBadge } from './badges'
import ChatPreview from './chat-preview'
import { CAMPAIGNS_PATH } from './constants'
import EmptyValue from './empty-value'
import LastLessonCell, { LastLessonContent } from './last-lesson-cell'
import MessageStudentButton from './message-student-button'
import {
  formatCampaignDate,
  formatRate,
  formatShortDate,
  studentName,
  toCampaignState,
} from './format'
import useStyles from './styles'

// Section title above its content, as on Coach Analytics. `subtitle` sits
// beside the title as supporting text.
const Section = ({ title, subtitle, children }) => {
  const analyticsClasses = useAnalyticsStyles()
  const classes = useStyles()
  return (
    <Box className={analyticsClasses.section}>
      <Box className={classes.sectionHeading}>
        <SectionTitle size="large" className={analyticsClasses.sectionTitle}>
          {title}
        </SectionTitle>
        {subtitle ? (
          <Typography className={classes.sectionSubtitle}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {children}
    </Box>
  )
}

Section.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  children: PropTypes.node.isRequired,
}

const InfoTip = ({ label, children }) => {
  const classes = useStyles()
  return (
    <Tooltip title={children} arrow placement="top" interactive>
      <IconButton
        size="small"
        aria-label={`About ${label}`}
        className={classes.infoButton}
      >
        <InfoOutlinedIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  )
}

InfoTip.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.string.isRequired,
}

const BreakdownRow = ({ label, value, success }) => {
  const classes = useStyles()
  return (
    <Box className={classes.breakdownRow}>
      <span
        className={clsx(classes.breakdownDot, success && classes.successDot)}
      />
      <span className={classes.breakdownLabel}>{label}</span>
      <span className={classes.breakdownValue}>{value}</span>
    </Box>
  )
}

BreakdownRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  success: PropTypes.bool,
}

// Six neutral tiles in funnel order. Total bookings is the sum of the two
// attributed figures the API already returns; the breakdown keeps them apart.
const CampaignPerformance = ({ results, attributionEndsAt }) => {
  const classes = useStyles()
  const readRate =
    results.readCount == null || !results.recipientCount
      ? null
      : results.readCount / results.recipientCount
  const totalBookings =
    results.directBookingCount + results.influencedBookingCount

  return (
    <Box className={classes.performanceGrid}>
      <StatCard
        label="Sent"
        value={results.recipientCount}
        caption="Students messaged"
        valueClassName={classes.metricValue}
      />
      <StatCard
        label="Read rate"
        value={formatRate(readRate)}
        caption={
          results.readCount == null
            ? 'Couldn’t load read counts.'
            : `${results.readCount} of ${results.recipientCount} read`
        }
        labelAction={
          <InfoTip label="Read rate">
            Students who read the message in chat, out of everyone it was sent
            to.
          </InfoTip>
        }
        valueClassName={classes.metricValue}
      />
      <StatCard
        label="Link clicks"
        value={results.linkVisitCount}
        caption="Students who opened a booking link"
        labelAction={
          <InfoTip label="Link clicks">
            Students who opened one of the booking links in this message.
          </InfoTip>
        }
        valueClassName={classes.metricValue}
      />
      <StatCard
        label="Replies"
        value={results.repliedCount}
        caption="Students who replied in chat"
        valueClassName={classes.metricValue}
      />
      <StatCard
        label="Total bookings"
        value={totalBookings}
        caption={`Within ${results.attributionWindowDays} days of sending${
          attributionEndsAt
            ? ` · counts through ${formatShortDate(attributionEndsAt)}`
            : ''
        }`}
        labelAction={
          <InfoTip label="Total bookings">
            {`Bookings from these students within ${results.attributionWindowDays} days of sending. A booking that used a campaign card counts only as “From link.”`}
          </InfoTip>
        }
        valueClassName={classes.metricValue}
      >
        <Box className={classes.breakdown}>
          <BreakdownRow
            success
            label="From link"
            value={results.directBookingCount}
          />
          <BreakdownRow
            label="Booked later"
            value={results.influencedBookingCount}
          />
        </Box>
      </StatCard>
      <StatCard
        label="Reactivation rate"
        value={formatRate(results.reactivationRate)}
        caption={
          results.reactivationEligibleCount > 0
            ? `${results.reactivatedCount} of ${results.reactivationEligibleCount} students with no lesson in ${results.reactivationLookbackDays} days booked again.`
            : `No one in this campaign had gone ${results.reactivationLookbackDays} days without a lesson.`
        }
        labelAction={
          <InfoTip label="Reactivation rate">
            {`Share of students who hadn’t had a lesson in ${results.reactivationLookbackDays} days before this campaign and booked again.`}
          </InfoTip>
        }
        valueClassName={classes.metricValue}
      />
    </Box>
  )
}

CampaignPerformance.propTypes = {
  results: PropTypes.shape({
    recipientCount: PropTypes.number.isRequired,
    readCount: PropTypes.number,
    repliedCount: PropTypes.number.isRequired,
    linkVisitCount: PropTypes.number.isRequired,
    directBookingCount: PropTypes.number.isRequired,
    influencedBookingCount: PropTypes.number.isRequired,
    reactivationEligibleCount: PropTypes.number.isRequired,
    reactivatedCount: PropTypes.number.isRequired,
    reactivationRate: PropTypes.number,
    attributionWindowDays: PropTypes.number.isRequired,
    reactivationLookbackDays: PropTypes.number.isRequired,
  }).isRequired,
  attributionEndsAt: PropTypes.string,
}

// The last column has no heading; its header text is for screen readers only.
const ACTIONS_COLUMN = {
  id: 'actions',
  label: <span style={visuallyHidden}>Message</span>,
  align: 'right',
  width: '8%',
}

const RECIPIENT_COLUMNS = [
  { id: 'student', label: 'Student', align: 'left', width: '28%' },
  { id: 'lastBookedAt', label: 'Last lesson', align: 'left', width: '20%' },
  { id: 'engagement', label: 'Engagement', align: 'left', width: '22%' },
  { id: 'booking', label: 'Booking status', align: 'left', width: '22%' },
  ACTIONS_COLUMN,
].map(column => ({ ...column, mobile: true, sortable: false }))

// Phones get three columns: who (with their last lesson), what happened, and
// the Message button.
const COMPACT_RECIPIENT_COLUMNS = [
  { id: 'student', label: 'Student', align: 'left', width: '52%' },
  { id: 'activity', label: 'Activity', align: 'left', width: '32%' },
  { ...ACTIONS_COLUMN, width: '16%' },
].map(column => ({ ...column, mobile: true, sortable: false }))

const noop = () => {}

// The API reports per-student link opens and replies, not per-student reads,
// so "Read" / "Not read" are not shown here (the Read rate tile has the total).
const EngagementContent = ({ recipient }) => {
  const classes = useStyles()
  const nameAgeClasses = useStudentListNameAgeStyles()
  const { visitedAt, repliedAt } = recipient
  const signals = [
    repliedAt && { label: 'Replied', at: repliedAt },
    visitedAt && { label: 'Link opened', at: visitedAt },
  ].filter(Boolean)

  if (signals.length === 0) return <EmptyValue label="No activity yet" />

  const [primary, ...others] = signals
  const details = signals
    .map(({ label, at }) => `${label} ${formatShortDate(at)}`)
    .join(' · ')
  return (
    <Tooltip title={details} placement="top-start">
      <Box>
        <Typography className={nameAgeClasses.text}>{primary.label}</Typography>
        <Typography className={classes.recipientCaption} noWrap>
          {others.length
            ? `${others[0].label} ${formatShortDate(others[0].at)}`
            : formatShortDate(primary.at)}
        </Typography>
      </Box>
    </Tooltip>
  )
}

EngagementContent.propTypes = {
  recipient: PropTypes.shape({
    visitedAt: PropTypes.string,
    repliedAt: PropTypes.string,
  }).isRequired,
}

const EngagementCell = ({ recipient }) => (
  <TableCell>
    <EngagementContent recipient={recipient} />
  </TableCell>
)

EngagementCell.propTypes = EngagementContent.propTypes

const BookingStatusContent = ({ recipient }) => {
  const classes = useStyles()
  const nameAgeClasses = useStudentListNameAgeStyles()
  const { conversionKind } = recipient

  if (!conversionKind) return <EmptyValue label="Not booked" />

  return (
    <Box className={classes.bookedStatus}>
      <span className={clsx(classes.breakdownDot, classes.successDot)} />
      <Box>
        <Typography className={nameAgeClasses.text}>Booked</Typography>
        <Typography className={classes.recipientCaption} noWrap>
          {conversionKind === 'DIRECT'
            ? 'From campaign link'
            : 'Without campaign link'}
        </Typography>
      </Box>
    </Box>
  )
}

BookingStatusContent.propTypes = {
  recipient: PropTypes.shape({
    conversionKind: PropTypes.oneOf(['DIRECT', 'INFLUENCED']),
  }).isRequired,
}

const BookingStatusCell = ({ recipient }) => (
  <TableCell>
    <BookingStatusContent recipient={recipient} />
  </TableCell>
)

BookingStatusCell.propTypes = BookingStatusContent.propTypes

const RecipientRow = ({ recipient, compact }) => {
  const classes = useStyles()
  const nameAgeClasses = useStudentListNameAgeStyles()
  const name = studentName(recipient.student)
  const studentId = recipient.student?.id
  const nameCell = (
    <Box className={nameAgeClasses.nameCell}>
      <Avatar className={nameAgeClasses.studentAvatar}>
        {getInitials(name)}
      </Avatar>
      <Box className={nameAgeClasses.info}>
        <Typography
          className={clsx(nameAgeClasses.text, classes.studentLinkName)}
          noWrap
        >
          {name}
        </Typography>
        {recipient.contactName && recipient.contactName !== name ? (
          <Typography className={classes.recipientCaption} noWrap>
            Sent to {recipient.contactName}
          </Typography>
        ) : null}
      </Box>
    </Box>
  )
  const student = studentId ? (
    <RouterLink
      to={`/pga-coach/student/${studentId}`}
      className={classes.studentLink}
      aria-label={`View ${name || 'student'}`}
    >
      {nameCell}
    </RouterLink>
  ) : (
    nameCell
  )

  if (compact) {
    return (
      <TableRow>
        <TableCell className={nameAgeClasses.info}>
          {student}
          <Box className={classes.compactLastLesson}>
            <Typography className={classes.recipientCaption}>
              Last lesson
            </Typography>
            <LastLessonContent recipient={recipient} />
          </Box>
        </TableCell>
        <TableCell>
          <Box className={classes.compactActivity}>
            <EngagementContent recipient={recipient} />
            <BookingStatusContent recipient={recipient} />
          </Box>
        </TableCell>
        <TableCell className={classes.actionsCell}>
          <MessageStudentButton recipient={recipient} />
        </TableCell>
      </TableRow>
    )
  }

  return (
    <TableRow>
      <TableCell className={nameAgeClasses.info}>{student}</TableCell>
      <LastLessonCell recipient={recipient} />
      <EngagementCell recipient={recipient} />
      <BookingStatusCell recipient={recipient} />
      <TableCell className={classes.actionsCell}>
        <MessageStudentButton recipient={recipient} />
      </TableCell>
    </TableRow>
  )
}

RecipientRow.propTypes = {
  compact: PropTypes.bool,
  recipient: PropTypes.shape({
    contactName: PropTypes.string,
    lastBookedAt: PropTypes.string,
    visitedAt: PropTypes.string,
    repliedAt: PropTypes.string,
    conversionKind: PropTypes.oneOf(['DIRECT', 'INFLUENCED']),
    student: PropTypes.shape({
      id: PropTypes.string,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
    }),
  }).isRequired,
}

// Same list table as the coach Students page (EnhancedTable `list` variant:
// 56px rows, 36px initials avatars).
const RecipientsTable = ({ recipients }) => {
  const classes = useStyles()
  const compact = useIsMobile()
  return (
    <Box className={classes.recipientsTable}>
      <EnhancedTable
        label="Campaign students"
        rows={recipients.map(recipient => (
          <RecipientRow
            key={recipient.id}
            recipient={recipient}
            compact={compact}
          />
        ))}
        columns={compact ? COMPACT_RECIPIENT_COLUMNS : RECIPIENT_COLUMNS}
        numberOfRows={recipients.length}
        variant="list"
        rowHeight="56px"
        page={0}
        onPageChange={noop}
        showHeaderPagination={false}
        showPaginator={false}
      />
    </Box>
  )
}

RecipientsTable.propTypes = {
  recipients: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      contactName: PropTypes.string,
      lastBookedAt: PropTypes.string,
      visitedAt: PropTypes.string,
      repliedAt: PropTypes.string,
      conversionKind: PropTypes.oneOf(['DIRECT', 'INFLUENCED']),
      student: PropTypes.shape({
        firstName: PropTypes.string,
        lastName: PropTypes.string,
      }),
    }),
  ).isRequired,
}

const LONG_MESSAGE_LENGTH = 240
const LONG_MESSAGE_LINES = 6
const isLongMessage = body =>
  Boolean(body) &&
  (body.length > LONG_MESSAGE_LENGTH ||
    body.split('\n').length > LONG_MESSAGE_LINES)

// Compact read-only copy of what was sent. Long messages are clamped, with
// the full text in a dialog.
const MessagePreview = ({ preview }) => {
  const classes = useStyles()
  const dialogClasses = useDialogTitleStyles()
  const [open, setOpen] = useState(false)
  const long = isLongMessage(preview.body)
  const chat = clamp => (
    <ChatPreview
      alignStart
      showLabel={false}
      clampBody={clamp}
      body={preview.body}
      offerings={preview.offerings}
      media={preview.media}
    />
  )

  return (
    <>
      {chat(long)}
      {long ? (
        <Button
          color="primary"
          size="small"
          className={classes.viewFullMessage}
          onClick={() => setOpen(true)}
        >
          View full message
        </Button>
      ) : null}
      <Dialog
        isOpen={open}
        onClose={() => setOpen(false)}
        showCloseButton
        fullWidth
        maxWidth="sm"
        classes={{ paper: dialogClasses.dialogPaper }}
        aria-labelledby="campaign-message-title"
      >
        {() => (
          <>
            <DialogTitle
              disableTypography
              classes={{ root: dialogClasses.titleRoot }}
            >
              <Typography
                id="campaign-message-title"
                component="h2"
                className={dialogClasses.title}
              >
                Message
              </Typography>
            </DialogTitle>
            <DialogContent className={classes.fullMessageContent}>
              {chat(false)}
            </DialogContent>
          </>
        )}
      </Dialog>
    </>
  )
}

MessagePreview.propTypes = {
  preview: PropTypes.shape({
    body: PropTypes.string,
    offerings: PropTypes.array.isRequired,
    media: PropTypes.array.isRequired,
  }).isRequired,
}

export function CampaignResultsView({ campaign, busy, onDuplicate, onCancel }) {
  const classes = useStyles()
  const analyticsClasses = useAnalyticsStyles()
  const dialogClasses = useDialogTitleStyles()
  const isMobile = useIsMobile()
  const [confirmCancel, setConfirmCancel] = useState(false)
  const preview = toCampaignState(campaign)
  const sent = campaign.status === 'SENT'
  const recipientCount = campaign.results?.recipientCount ?? 0

  const actions = (
    <Box display="flex" gridGap={8}>
      {campaign.cancelable ? (
        <Button
          variant="outlined"
          color="primary"
          className={classes.pillButton}
          disabled={busy}
          onClick={() => setConfirmCancel(true)}
        >
          Cancel Send
        </Button>
      ) : null}
      <Button
        variant="outlined"
        color="primary"
        className={classes.pillButton}
        disabled={busy}
        startIcon={<FileCopyIcon />}
        onClick={onDuplicate}
      >
        Duplicate
      </Button>
    </Box>
  )

  return (
    <>
      <PageHeader
        title={campaign.name}
        backTo={CAMPAIGNS_PATH}
        backLabel="Back to campaigns"
        avatar={
          isMobile ? (
            undefined
          ) : (
            <Avatar className={classes.headerAvatar}>
              <CampaignOutlinedIcon />
            </Avatar>
          )
        }
        subtitle={
          <Box component="span" className={classes.headerMeta}>
            <StatusBadge status={campaign.status} />
            <span>{formatCampaignDate(campaign)}</span>
          </Box>
        }
        actions={actions}
        fixedMobileActions
        gridClassName={classes.headerGrid}
      />
      {busy ? <LinearProgress color="primary" /> : null}
      <Box className={clsx(analyticsClasses.container, classes.resultsPage)}>
        <Box className={classes.resultsLayout}>
          <Box className={classes.resultsMain}>
            <Section title="Campaign performance">
              {sent ? (
                <CampaignPerformance
                  results={campaign.results}
                  attributionEndsAt={campaign.attributionEndsAt}
                />
              ) : (
                <Box className={classes.emptyPanel}>
                  <Typography className={classes.helperText}>
                    {campaign.status === 'SCHEDULED'
                      ? 'Results show here once the campaign sends.'
                      : 'This campaign was canceled. Duplicate it to send it again.'}
                  </Typography>
                </Box>
              )}
            </Section>

            {sent && campaign.recipients.length > 0 ? (
              <Section
                title="Students"
                subtitle={
                  campaign.recipients.length < recipientCount
                    ? `Showing ${campaign.recipients.length.toLocaleString()} of ${recipientCount.toLocaleString()} students`
                    : formatCount(campaign.recipients.length, 'student')
                }
              >
                <Box className={classes.tableShell}>
                  <RecipientsTable recipients={campaign.recipients} />
                </Box>
              </Section>
            ) : null}
          </Box>

          <Box className={classes.resultsAside}>
            <Section title="Message">
              <MessagePreview preview={preview} />
            </Section>
          </Box>
        </Box>
      </Box>

      <Dialog
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        showCloseButton
        fullWidth
        maxWidth="xs"
        classes={{ paper: dialogClasses.dialogPaper }}
        aria-labelledby="cancel-campaign-title"
      >
        {({ closeDialog }) => (
          <>
            <DialogTitle
              disableTypography
              classes={{ root: dialogClasses.titleRoot }}
            >
              <Typography
                id="cancel-campaign-title"
                component="h2"
                className={dialogClasses.title}
              >
                Cancel this campaign?
              </Typography>
              <Typography className={dialogClasses.bodyText}>
                It won’t be sent. You can duplicate it later to send it again.
              </Typography>
            </DialogTitle>
            <DialogActions className={dialogClasses.actions}>
              <Button color="primary" size="large" onClick={closeDialog}>
                Keep It
              </Button>
              <Button
                color="primary"
                variant="contained"
                size="large"
                disableElevation
                onClick={() => {
                  setConfirmCancel(false)
                  onCancel()
                }}
              >
                Cancel Send
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  )
}

CampaignResultsView.propTypes = {
  campaign: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    cancelable: PropTypes.bool,
    attributionEndsAt: PropTypes.string,
    results: PropTypes.object,
    recipients: PropTypes.array.isRequired,
  }).isRequired,
  busy: PropTypes.bool,
  onDuplicate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
}

const CampaignResults = () => {
  const { id } = useParams()
  const history = useHistory()
  const { user } = useAuth()
  const hasPaidTier = Boolean(user?.coach?.hasPaidTierSubscription)
  const { enqueueSnackbar } = useSnackbar()
  const showError = useSnackbarError(enqueueSnackbar)
  const showSuccess = useSnackbarSuccess(enqueueSnackbar)
  const [busy, setBusy] = useState(false)
  const { data, loading, error, refetch } = useCoachCampaignResults(id, {
    skip: !hasPaidTier,
  })
  const [duplicateCampaign] = useDuplicateCoachCampaign()
  const [cancelCampaign] = useCancelCoachCampaign()

  if (loading) return <LinearProgress color="primary" />
  if (error) return <Error error={error} />
  const campaign = data?.coachCampaign
  if (!campaign) return hasPaidTier ? <Error statusCode={404} /> : null
  if (campaign.status === 'DRAFT') {
    return <Redirect to={`${CAMPAIGNS_PATH}/${id}/edit`} />
  }

  const run = async (mutate, key, onSuccess) => {
    setBusy(true)
    let response
    try {
      const { data: result } = await mutate({ variables: { id } })
      response = result?.[key]
      if (!response?.success) showError(response?.message)
    } catch (e) {
      showError()
    }
    setBusy(false)
    if (response?.success) onSuccess(response)
  }

  return (
    <CampaignResultsView
      campaign={campaign}
      busy={busy}
      onDuplicate={() =>
        run(duplicateCampaign, 'duplicateCoachCampaign', response => {
          showSuccess('Copied to a new draft')
          history.push(`${CAMPAIGNS_PATH}/${response.campaign.id}/edit`)
        })
      }
      onCancel={() =>
        run(cancelCampaign, 'cancelCoachCampaign', () => {
          showSuccess('Campaign canceled')
          refetch()
        })
      }
    />
  )
}

export default CampaignResults
